/* assets/src/viewer/models/generic.js
 * Pergola generica con telo (struttura + perimetro + canopy). Usata per i tipi 'fabric'/'glass'
 * e come FALLBACK sicuro (sostituisce il bug buildEmergencyPergolaPreview, mai definito).
 * Aggiornamento in-place (reposiziona pilastri, scala travi/canopy): nessun dispose nel drag.
 */
import * as THREE from 'three';
import { getStructuralSupportPositions } from './support.js';

const LEG = 0.15;
const BEAM_H = 0.25;
const BEAM_W = 0.15;
const H = 2.4;
const MAX_SUPPORTS = 4;

export class GenericPergolaModel {
  constructor(materials, colors, opts = {}) {
    this.type = opts.type || 'fabric';
    this.materials = materials;
    this.group = new THREE.Group();
    this.glass = this.type === 'glass';

    this.matStruttura = materials.structureMaterial(colors.structColor);
    if (this.glass) {
      this.matCanopy = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(colors.fabricColor), metalness: 0, roughness: 0.08,
        transmission: 0.9, thickness: 0.4, ior: 1.4, transparent: true, envMapIntensity: 1.0,
      });
      this.matCanopy.userData.asgRole = 'glass';
    } else {
      this.matCanopy = materials.fabricMaterial(colors.fabricColor);
    }

    this._build();
  }

  _makePillar() {
    const m = new THREE.Mesh(new THREE.BoxGeometry(LEG, H, LEG), this.matStruttura);
    m.castShadow = true; m.receiveShadow = true;
    return m;
  }

  _makeBeam(material) {
    // lunghezza unitaria in X (scalata via scale.x)
    const m = new THREE.Mesh(new THREE.BoxGeometry(1, BEAM_H, BEAM_W), material || this.matStruttura);
    m.castShadow = true; m.receiveShadow = true;
    return m;
  }

  _build() {
    this.corners = [];
    for (let i = 0; i < 4; i++) { const p = this._makePillar(); this.group.add(p); this.corners.push(p); }
    this.supports = [];
    for (let i = 0; i < MAX_SUPPORTS; i++) { const p = this._makePillar(); p.visible = false; this.group.add(p); this.supports.push(p); }

    this.frontBeam = this._makeBeam(); this.group.add(this.frontBeam);
    this.rearBeam = this._makeBeam(); this.group.add(this.rearBeam);
    this.sideBeam1 = this._makeBeam(); this.sideBeam1.rotation.y = Math.PI / 2; this.group.add(this.sideBeam1);
    this.sideBeam2 = this._makeBeam(); this.sideBeam2.rotation.y = Math.PI / 2; this.group.add(this.sideBeam2);

    // canopy (telo/vetro) sopra la struttura, scalata su w,d
    this.fabricGroup = new THREE.Group();
    this.canopy = new THREE.Mesh(new THREE.BoxGeometry(1, this.glass ? 0.02 : 0.05, 1), this.matCanopy);
    this.canopy.castShadow = !this.glass; this.canopy.receiveShadow = true;
    this.fabricGroup.add(this.canopy);
    this.group.add(this.fabricGroup);

    // copertura apri/chiudi: scala la canopy lungo Z (0 = ritirata verso il fondo)
    this.fabricGroup.userData.animationSpeed = 0.02;
    this.fabricGroup.userData.updateLayout = (prog) => this.updateLayout(prog);
  }

  _layout(w, d, config) {
    const yBeam = H + BEAM_H / 2;
    const c = this.corners;
    c[0].position.set(-w / 2 + LEG / 2, H / 2, d / 2 - LEG / 2);
    c[1].position.set(w / 2 - LEG / 2, H / 2, d / 2 - LEG / 2);
    c[2].position.set(-w / 2 + LEG / 2, H / 2, -d / 2 + LEG / 2);
    c[3].position.set(w / 2 - LEG / 2, H / 2, -d / 2 + LEG / 2);

    this.frontBeam.scale.x = w; this.frontBeam.position.set(0, yBeam, d / 2 - LEG / 2);
    this.rearBeam.scale.x = w; this.rearBeam.position.set(0, yBeam, -d / 2 + LEG / 2);
    const sideLen = d;
    this.sideBeam1.scale.x = sideLen; this.sideBeam1.position.set(-w / 2 + LEG / 2, yBeam, 0);
    this.sideBeam2.scale.x = sideLen; this.sideBeam2.position.set(w / 2 - LEG / 2, yBeam, 0);

    const positions = getStructuralSupportPositions(config || {}, w).slice(0, MAX_SUPPORTS);
    for (let i = 0; i < MAX_SUPPORTS; i++) {
      const p = this.supports[i];
      if (i < positions.length) { p.visible = true; p.position.set(positions[i], H / 2, d / 2 - LEG / 2); }
      else p.visible = false;
    }

    this._dims = { w, d };
    this.fabricGroup.position.set(0, H + BEAM_H + (this.glass ? 0.02 : 0.04), 0);
    this._applyCanopy(typeof window.ASG_TeloProgress === 'number' ? window.ASG_TeloProgress : 1);
  }

  _applyCanopy(prog) {
    if (!this._dims) return;
    const open = THREE.MathUtils.clamp(prog, 0, 1); // 1 = estesa
    const w = this._dims.w; const d = this._dims.d;
    this.canopy.scale.set(w + 0.1, 1, Math.max(0.05, d * open + 0.05));
    // ritrae verso il fondo (z negativo)
    this.canopy.position.set(0, 0, (d / 2) - (d * open) / 2);
  }

  build(dims, config) { this.update(dims, config); }

  update(dims, config = null) {
    this._lastConfig = config || this._lastConfig || {};
    this._layout(dims.w, dims.d, this._lastConfig);
  }

  setStructureColor(color) { this.materials.crossfadeColor(this.matStruttura, color); }
  setFabricColor(color) { this.materials.crossfadeColor(this.matCanopy, color); }

  updateLayout(prog) { this._applyCanopy(prog); }

  dispose() {
    this.group.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
    [this.matStruttura, this.matCanopy].forEach((m) => m && m.dispose());
  }
}
